import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';

import CommonDropdownFilter from "../../components/filters/CommonDropdown.filter";
import CommonIncludeInactiveFilter from "../../components/filters/CommonIncludeInactive.filter";

import { ServiceLocationContext } from '../../utils/context/ServiceLocationContext';
import { getDropdownValue } from '../../components/finalForms/libs/data/dropdownUtilities';
import { useLazyGetAllDepartmentsQuery } from '../../app/services/departmentAPI';
import { useLazyGetAllClientsQuery } from '../../app/services/clientAPI';
import { useLazyGetAllInvoiceNumberQuery } from '../../app/services/productSalesAPI';

const ProductSalesFilter = forwardRef(({ onFilterChange, lProductSaleKey, selectedClientKey, selectedDepartmentKey, onInvoiceDataLoad, onInvoiceChange }, ref) => {

    //Dropdown State for Set the Dropdown
    const [client, setClient] = useState([]);
    const [department, setDepartment] = useState([]);
    const [invoiceAmount, setInvoiceAmount] = useState([]);
    const [filters, setFilters] = useState({
        client: null,
        department: null,
        invoiceNo: null,
    });


    const [includeInactive, setIncludeInactive] = useState(false);

    const { selectedLocation } = useContext(ServiceLocationContext);
    const locationkey = getDropdownValue(selectedLocation);

    // RTK Query API's for Dropdown 
    const [getAllClients, {
        isFetching: isAllClientsFetching,
        isSuccess: isAllClientsFetched,
        data: allClientsData,
    },] = useLazyGetAllClientsQuery();

    const [getAllDepartments, {
        isFetching: isAllDepartmentsFetching,
        isSuccess: isAllDepartmentsFetched,
        data: allDepartmentsData,
    },] = useLazyGetAllDepartmentsQuery();

    const [getAllInvoiceNumber, {
        isFetching: isGetAllInvoiceNumberFetching,
        isSuccess: isGetAllInvoiceNumberFetched,
        data: allInvoiceNumberData,
    },] = useLazyGetAllInvoiceNumberQuery();


    useEffect(() => {
        if (locationkey) {
            getAllClients(locationkey)
        }
    }, [locationkey])

    useEffect(() => {
        if (locationkey && filters.client?.value) {
            const req = {
                plDepartmentKey: 0,
                plClientKey: filters.client?.value,
                pbIncludeInactive: includeInactive,
                plServiceLocationKey: locationkey
            }
            getAllDepartments(req)
        }
    }, [locationkey, filters.client])

    useEffect(() => {
        if (locationkey && filters.department?.value) {
            const req = {
                plProductSaleKey: 0,
                plDepartmentKey: filters.department?.value,
                pbIncludeCanceled: includeInactive,
            }
            getAllInvoiceNumber(req)
        }
    }, [locationkey, filters.department, includeInactive])

    useEffect(() => {
        if (!isAllClientsFetching && isAllClientsFetched && allClientsData) {
            const dropDownField = allClientsData.map((item) => ({
                label: item.sClientName1,
                value: item.lClientKey,
            }));
            setClient(dropDownField)

            let selectedOption = null;

            if (selectedClientKey) {
                selectedOption = dropDownField.find(
                    (x) => x.value === selectedClientKey
                );
            }

            if (!selectedOption && dropDownField.length > 0) {
                selectedOption = dropDownField[0];
            }

            if (selectedOption) {
                setFilters((prev) => ({
                    ...prev,
                    client: selectedOption
                }));

                if (onFilterChange) {
                    onFilterChange({
                        selectedClientKey: selectedOption?.value,
                        selectedDepartmentKey: null
                    });
                }
            }
        }
    }, [isAllClientsFetching, isAllClientsFetched, allClientsData, selectedClientKey]);

    useEffect(() => {
        if (!isAllDepartmentsFetching && isAllDepartmentsFetched && allDepartmentsData) {

            const dropDownField = allDepartmentsData.map((item) => ({
                label: item.sDepartmentName,
                value: item.lDepartmentKey,
            }));
            setDepartment(dropDownField)

            let selectedOption = null;

            if (selectedDepartmentKey) {
                selectedOption = dropDownField.find(
                    (x) => x.value === selectedDepartmentKey
                );
            }

            if (!selectedOption && dropDownField.length > 0) {
                selectedOption = dropDownField[0];
            }

            if (selectedOption) {
                setFilters((prev) => ({
                    ...prev,
                    department: selectedOption
                }));

                if (onFilterChange) {
                    onFilterChange({
                        selectedClientKey: filters.client?.value || null,
                        selectedDepartmentKey: selectedOption?.value
                    });
                }
            }
        }

    }, [isAllDepartmentsFetching, isAllDepartmentsFetched, allDepartmentsData, selectedDepartmentKey]);

    useEffect(() => {
        if (!isGetAllInvoiceNumberFetching && isGetAllInvoiceNumberFetched && allInvoiceNumberData) {

            const dropDownField = allInvoiceNumberData.map((item) => ({
                label: item.sInvoiceNumber,
                value: item.lProductSaleKey,
            }));

            setInvoiceAmount(dropDownField)
            if (onInvoiceDataLoad) {
                onInvoiceDataLoad(allInvoiceNumberData);
            }
            let selectedOption = null;

            if (lProductSaleKey) {
                selectedOption = dropDownField.find(
                    (x) => x.value === lProductSaleKey
                );
            }

            if (!selectedOption && dropDownField.length > 0) {
                selectedOption = dropDownField[0];
            }

            if (selectedOption) {
                setFilters((prev) => ({
                    ...prev,
                    invoiceNo: selectedOption
                }));
            }
            onInvoiceChange?.(selectedOption?.value);
        }

    }, [isGetAllInvoiceNumberFetching, isGetAllInvoiceNumberFetched, allInvoiceNumberData]);


    // onChange for Client and Department
    const handleDropdownChange = (type, selectedOption) => {
        const updatedFilters = {
            ...filters,
            [type]: selectedOption,
            ...(type === "client" && { department: null, invoiceNo: null })
        };

        setFilters(updatedFilters);
        if (type === "invoiceNo") {
            onInvoiceChange?.(selectedOption?.value || null);
        }

        if (onFilterChange) {
            onFilterChange({
                selectedClientKey: updatedFilters.client?.value || null,
                selectedDepartmentKey: updatedFilters.department?.value || null,
            });
        }
    };

    const handleCheckboxChange = () => {
        setIncludeInactive(!includeInactive);
    };

    useImperativeHandle(ref, () => ({
        reloadInvoiceDropdown() {
            if (filters.department?.value) {
                const req = {
                    plProductSaleKey: 0,
                    plDepartmentKey: filters.department.value,
                    pbIncludeCanceled: includeInactive,
                };

                getAllInvoiceNumber(req);
            }
        }
    }));

    return (
        <div className="row">
            <div className="col-md-4 my-2 status-spacing">
                <CommonDropdownFilter
                    label={"Client : "}
                    placeholder="Select Client"
                    listOptions={client}
                    selectedValue={filters.client?.value}
                    onChange={(option) =>
                        handleDropdownChange("client", option)
                    }
                />
            </div>
            <div className="col-md-3 my-2 large-spacing">
                <CommonDropdownFilter
                    label={"Department : "}
                    placeholder="Select Department"
                    listOptions={department}
                    selectedValue={filters.department?.value}
                    onChange={(option) =>
                        handleDropdownChange("department", option)
                    }
                />
            </div>
            <div className="col-md-3 my-2 large-spacing">
                <CommonDropdownFilter
                    label={"Invoice # : "}
                    placeholder="Select Invoice #"
                    listOptions={invoiceAmount}
                    selectedValue={filters.invoiceNo?.value}
                    onChange={(option) =>
                        handleDropdownChange("invoiceNo", option)
                    }
                />
            </div>
            <div className="col-md-2 my-2 align-items-center">
                <div className="checkbox-only h-100 d-flex align-items-center">
                    <CommonIncludeInactiveFilter
                        // name={"inlucdeInactive"}
                        dataField="includeInactive"
                        // label="Show Inactive"
                        checked={includeInactive}
                        onChange={handleCheckboxChange}
                    />
                </div>
            </div>
        </div>
    )
})

export default ProductSalesFilter